import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../context/Api';

const CourseWorkResults = () => {
    const { courseworkId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [marks, setMarks] = useState('');
    const [feedback, setFeedback] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [newResult, setNewResult] = useState({
        score: '',
        comments: ''
    });
    const [isSearching, setIsSearching] = useState(false);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [selectedComment, setSelectedComment] = useState({ student: null, comment: '' });

    useEffect(() => {
        fetchCourseworkDetails();
    }, [courseworkId]);

    const fetchCourseworkDetails = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/courseworks/${courseworkId}/marks`);
            setData(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch coursework details');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddMarks = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${BASE_URL}/api/courseworks/${courseworkId}/marks`, {
                studentId: selectedStudent.RegNumber,
                marks: parseInt(marks),
                feedback
            });
            
            setIsModalOpen(false);
            setSelectedStudent(null);
            setMarks('');
            setFeedback('');
            fetchCourseworkDetails();
        } catch (err) {
            setError('Failed to add marks');
            console.error(err);
        }
    };

    const searchStudents = useCallback(async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const response = await axios.get(`${BASE_URL}/subjectlevelclasses-students/get-students-class/${data?.coursework?.ClassID}`, {
                params: { search: query }
            });
            setSearchResults(response.data.students);
        } catch (error) {
            console.error('Error searching students:', error);
        } finally {
            setIsSearching(false);
        }
    }, [data?.coursework?.ClassID]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchQuery) {
                searchStudents(searchQuery);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery, searchStudents]);

    const handleAddResult = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${BASE_URL}/api/courseworks/${courseworkId}/marks`, {
                studentId: selectedStudent.RegNumber,
                marks: parseFloat(newResult.score),
                feedback: newResult.comments
            });
            
            setIsAddModalOpen(false);
            setSelectedStudent(null);
            setNewResult({ score: '', comments: '' });
            fetchCourseworkDetails();
        } catch (err) {
            setError('Failed to add result');
            console.error(err);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    if (!data) return null;

    const { coursework, students, stats } = data;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Back Button */}
            <button
                onClick={() => navigate(-1)}
                className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
            >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
            </button>

            {/* Coursework Details Header */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-4">{coursework.Description}</h1>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                        <span className="text-gray-600">Due Date:</span>
                        <p className="font-medium">{new Date(coursework.DueDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                        <span className="text-gray-600">Total Marks:</span>
                        <p className="font-medium">{coursework.TotalMarks}</p>
                    </div>
                    <div>
                        <span className="text-gray-600">Subject:</span>
                        <p className="font-medium">{coursework.Subject}</p>
                    </div>
                    <div>
                        <span className="text-gray-600">Class:</span>
                        <p className="font-medium">{coursework.ClassName}</p>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Total Students</div>
                    <div className="text-2xl font-bold text-gray-800">{stats.totalStudents}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Marked</div>
                    <div className="text-2xl font-bold text-purple-600">{stats.markedCount}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Average Mark</div>
                    <div className="text-2xl font-bold text-blue-600">{stats.averageMark}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Highest Mark</div>
                    <div className="text-2xl font-bold text-green-600">{stats.highestMark}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Lowest Mark</div>
                    <div className="text-2xl font-bold text-red-600">{stats.lowestMark}</div>
                </div>
            </div>

            {/* Add Result Button */}
            <div className="flex justify-end mb-6">
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add New Result
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
            )}

            {/* Students List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-800">Student Results</h2>
                        <span className="text-sm text-gray-500">
                            {stats.markedCount} of {stats.totalStudents} marked
                        </span>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Student
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Marks
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                                    Feedback
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {students.map((student) => (
                                <tr key={student.RegNumber} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {student.Name} {student.Surname}
                                        </div>
                                        <div className="text-sm text-gray-500">{student.RegNumber}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {student.AssignedMarks ? (
                                                <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">
                                                    {student.AssignedMarks}/{coursework.TotalMarks}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                                                    Not Marked
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {student.Feedback ? (
                                            <button
                                                onClick={() => {
                                                    setSelectedComment({
                                                        student: student,
                                                        comment: student.Feedback
                                                    });
                                                    setIsCommentModalOpen(true);
                                                }}
                                                className="text-sm text-gray-900 hover:text-purple-600 text-left"
                                            >
                                                <div className="line-clamp-2">
                                                    {student.Feedback}
                                                </div>
                                            </button>
                                        ) : (
                                            <span className="text-sm text-gray-500">No feedback</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <button
                                            onClick={() => {
                                                setSelectedStudent(student);
                                                setMarks(student.AssignedMarks || '');
                                                setFeedback(student.Feedback || '');
                                                setIsModalOpen(true);
                                            }}
                                            className="inline-flex items-center px-3 py-1 border border-purple-600 text-purple-600 rounded-md hover:bg-purple-50"
                                        >
                                            {student.AssignedMarks ? 'Edit Marks' : 'Add Marks'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Marks Modal */}
            {isModalOpen && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl p-8 w-full max-w-md">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                            {selectedStudent.AssignedMarks ? 'Edit' : 'Add'} Marks for {selectedStudent.Name} {selectedStudent.Surname}
                        </h3>
                        <form onSubmit={handleAddMarks} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Marks (out of {coursework.TotalMarks})
                                </label>
                                <input
                                    type="number"
                                    value={marks}
                                    onChange={(e) => setMarks(e.target.value)}
                                    min="0"
                                    max={coursework.TotalMarks}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Feedback
                                </label>
                                <textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    rows="3"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                                    placeholder="Add feedback (optional)"
                                />
                            </div>
                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                >
                                    Save Marks
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Result Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl p-8 w-full max-w-md">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                            Add New Result
                        </h3>
                        <form onSubmit={handleAddResult} className="space-y-4">
                            {!selectedStudent ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search Student
                                    </label>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                                        placeholder="Search by name or registration number..."
                                    />
                                    {isSearching && (
                                        <div className="text-sm text-gray-500 mt-2">Searching...</div>
                                    )}
                                    {searchResults.length > 0 && (
                                        <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                                            {searchResults.map((student) => (
                                                <button
                                                    key={student.RegNumber}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedStudent(student);
                                                        setSearchQuery('');
                                                        setSearchResults([]);
                                                    }}
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50"
                                                >
                                                    <div className="font-medium">{student.Name} {student.Surname}</div>
                                                    <div className="text-sm text-gray-500">{student.RegNumber}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <div className="font-medium">{selectedStudent.Name} {selectedStudent.Surname}</div>
                                        <div className="text-sm text-gray-500">{selectedStudent.RegNumber}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedStudent(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        Change
                                    </button>
                                </div>
                            )}

                            {selectedStudent && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Score (out of {coursework.TotalMarks})
                                        </label>
                                        <input
                                            type="number"
                                            value={newResult.score}
                                            onChange={(e) => setNewResult({...newResult, score: e.target.value})}
                                            max={coursework.TotalMarks}
                                            step="0.01"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Comments
                                        </label>
                                        <textarea
                                            value={newResult.comments}
                                            onChange={(e) => setNewResult({...newResult, comments: e.target.value})}
                                            rows="3"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                                            placeholder="Add comments (optional)"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsAddModalOpen(false);
                                        setSelectedStudent(null);
                                        setNewResult({ score: '', comments: '' });
                                        setSearchQuery('');
                                        setSearchResults([]);
                                    }}
                                    className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!selectedStudent || !newResult.score}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                >
                                    Save Result
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Comment Modal */}
            {isCommentModalOpen && selectedComment.student && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl p-8 w-full max-w-lg">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">
                                    Feedback for {selectedComment.student.Name} {selectedComment.student.Surname}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedComment.student.RegNumber}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsCommentModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-800 whitespace-pre-wrap">
                                {selectedComment.comment}
                            </p>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setIsCommentModalOpen(false)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseWorkResults; 